import clsx from "clsx"
import type { KeyboardEvent, ReactNode } from "react"

import { Image } from "../Image"
import styles from "./PreviewCard.module.css"

export interface PreviewCardProps {
  children: ReactNode
  /** Applies selected style, useful for the richtext field */
  selected?: boolean
  variant?: "compact" | "large"
  onClick?: () => void
}

export function PreviewCard(props: PreviewCardProps) {
  const { children, selected = false, variant = "compact", onClick } = props

  const clickable = Boolean(onClick)
  const clickableProps = clickable
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown,
      }
    : {}

  function onKeyDown(evt: KeyboardEvent) {
    if (evt.code === "Enter" || evt.code === "Space") onClick?.()
  }

  return (
    <div
      className={clsx(
        styles.previewCard,
        selected && styles.selected,
        variant === "compact" && styles.compact,
        variant === "large" && styles.large,
        clickable && styles.clickable,
      )}
      role="note"
      {...clickableProps}
    >
      {children}
    </div>
  )
}

interface PreviewImageProps {
  src: string
  alt?: string
  aspectRatio?: "1:1" | "16:9"
  framed?: boolean
  width?: number
  height?: number
}

export function PreviewImage(props: PreviewImageProps) {
  const { src, aspectRatio, framed, width, height, alt } = props

  return (
    <div
      className={clsx(
        styles.thumbnail,
        framed && styles.framed,
        aspectRatio === "1:1" && styles["thumbnail-1-1"],
        aspectRatio === "16:9" && styles["thumbnail-16-9"],
      )}
      style={{
        width,
        height,
      }}
    >
      <Image src={src} alt={alt} sizing="cover" animateOnLoad={false} />
    </div>
  )
}

interface PreviewContentProps {
  children: ReactNode
}

export function PreviewContent(props: PreviewContentProps) {
  const { children } = props

  return <div className={styles.content}>{children}</div>
}
